import { useState, useEffect } from "react";
import Input from "../components/Input";
import { T } from "../constants/theme";
import Btn from "../components/Btn";
import Badge from "../components/Badge";
import StepBar from "../components/StepBar";
import { API_BASE_URL } from "../config";

export default function SummaryStep({ data, studentId, password, onGoLogin }) {
  const [downloading, setDownloading] = useState(false);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);

    try {
      // Load docx library from CDN

      if (!window.docx) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");

          script.src = "https://unpkg.com/docx@8.5.0/build/index.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const {
        Document,
        Packer,
        Paragraph,
        TextRun,
        Table,
        TableRow,
        TableCell,
        AlignmentType,
        HeadingLevel,
        BorderStyle,
        WidthType,
        ShadingType,
      } = window.docx;

      const border = { style: BorderStyle.SINGLE, size: 1, color: "D9DEE6" };

      const borders = {
        top: border,
        bottom: border,
        left: border,
        right: border,
      };

      const rows = details.map(
        ([label, value], i) =>
          new TableRow({
            children: [
              new TableCell({
                borders,
                width: { size: 3000, type: WidthType.DXA },
                shading: { fill: "F1F5F9", type: ShadingType.CLEAR },
                margins: { top: 100, bottom: 100, left: 150, right: 150 },
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: label, bold: true, size: 22 }),
                    ],
                  }),
                ],
              }),

              new TableCell({
                borders,
                width: { size: 6360, type: WidthType.DXA },
                margins: { top: 100, bottom: 100, left: 150, right: 150 },
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: String(value), size: 22 })],
                  }),
                ],
              }),
            ],
          }),
      );

      const doc = new Document({
        styles: {
          default: { document: { run: { font: "Arial", size: 22 } } },
          paragraphStyles: [
            {
              id: "Heading1",
              name: "Heading 1",
              basedOn: "Normal",
              next: "Normal",
              quickFormat: true,
              run: { size: 32, bold: true, font: "Arial", color: "1E3A5F" },
              paragraph: {
                spacing: { before: 0, after: 240 },
                outlineLevel: 0,
              },
            },
          ],
        },

        sections: [
          {
            properties: {
              page: {
                size: { width: 12240, height: 15840 },
                margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
              },
            },

            children: [
              new Paragraph({
                heading: HeadingLevel.HEADING_1,
                children: [new TextRun("Shabbas Academy")],
              }),

              new Paragraph({
                children: [
                  new TextRun({
                    text: "Registration Confirmation",
                    bold: true,
                    size: 26,
                    color: "F4860A",
                  }),
                ],

                spacing: { after: 100 },
              }),

              new Paragraph({
                children: [
                  new TextRun({
                    text: "Registration completed successfully. Please keep these details safe for future login and examination activities.",
                    size: 22,
                    color: "475569",
                  }),
                ],

                spacing: { after: 280 },
              }),

              new Table({
                width: { size: 9360, type: WidthType.DXA },
                columnWidths: [3000, 6360],
                rows,
              }),

              new Paragraph({
                children: [
                  new TextRun({
                    text: "Important: Please save these credentials safely. They will be required for future login and examination activities.",
                    italics: true,
                    size: 20,
                    color: "475569",
                  }),
                ],

                spacing: { before: 280 },
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download =
        `${summary.studentId}_Registration_Details.docx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };


  useEffect(() => {
    const registrationId =
      sessionStorage.getItem("registrationId");

    if (registrationId) {
      loadSummary();
    }
  }, []);

  const loadSummary = async () => {
    try {
      const registrationId =
        sessionStorage.getItem("registrationId");

      const response = await fetch(
        `${API_BASE_URL}/api/Registration/${registrationId}/summary`
      );

      if (!response.ok) {
        throw new Error("Failed to load summary");
      }

      const result = await response.json();

      setSummary(result);
    }
    catch (err) {
      alert(err.message);
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const registrationId =
      sessionStorage.getItem(
        "registrationId"
      );

    const password =
      sessionStorage.getItem(
        "tempPassword"
      );

    const emailSent =
      sessionStorage.getItem(
        "emailSent"
      );

    if (
      registrationId &&
      password &&
      !emailSent
    ) {
      sendEmail(
        registrationId,
        password
      )
        .then(() => {
          sessionStorage.setItem(
            "emailSent",
            "true"
          );
        })
        .catch(err => {
          console.error(err);
        });
    }
  }, []);

  const sendEmail = async (
    registrationId,
    password
  ) => {
    const response = await fetch(
      `${API_BASE_URL}/api/Registration/${registrationId}/send-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          password
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        "Failed to send email"
      );
    }
  };


  const visiblePassword =
    password ||
    sessionStorage.getItem("tempPassword") ||
    "";

  const handleDownloadCredentials = () => {
    const content =
      `Shabbas Academy Login Credentials

Student ID / Username: ${summary.studentId}
Password: ${visiblePassword}

Student Name: ${summary.studentName}
School Name : ${summary.schoolName}
Standar: ${summary.class}
Mobile No.: ${summary.mobileNumber}

Please keep these credentials safe.
`;

    const blob = new Blob(
      [content],
      { type: "text/plain" }
    );

    const url =
      window.URL.createObjectURL(blob);

    const a =
      document.createElement("a");

    a.href = url;

    a.download =
      `${summary.studentId}_Credentials.txt`;

    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        Loading registration details...
      </div>
    );
  }

  if (!summary) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        Unable to load registration summary.
      </div>
    );
  }

  const handleGoLogin = () => {
    sessionStorage.removeItem(
      "registrationId"
    );

    sessionStorage.removeItem(
      "tempPassword"
    );

    sessionStorage.removeItem(
      "emailSent"
    );

    onGoLogin();
  };

  const details = [
    ["Student ID", summary.studentId],
    ["Username", summary.username],
    ["Password", password],
    ["Mobile Number", summary.mobileNumber],
    ["Student Name", summary.studentName],
    ["Class", summary.class],
    ["Exam Medium", summary.examMedium],
    ["School Name", summary.schoolName],
    [
      "Registration Date",
      new Date(summary.registrationDate).toLocaleDateString("en-IN")
    ],
    ["Payment Status", summary.paymentStatus],
  ];

  return (
    <div>
      <StepBar step={5} />

      <div style={{ textAlign: "center", marginBottom: 22 }}>
        <Badge color={T.green}>Step 5 of 5</Badge>

        <h2
          style={{
            fontSize: 20,
            fontWeight: 900,
            color: T.indigo,
            margin: "8px 0 4px",
          }}
        >
          Registration Completed Successfully
        </h2>

        <p style={{ fontSize: 13, color: T.gray600 }}>
          Welcome to Shabbas Academy, {summary.studentName}!
        </p>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 20
        }}
      >
        <img
          src={`${API_BASE_URL}/${summary.photoUrl}`}
          alt="Student"
          style={{
            width: 100,
            height: 100,
            borderRadius: 12,
            objectFit: "cover",
            border: `2px solid ${T.gray200}`
          }}
        />
      </div>

      {/* Credentials card */}

      <div
        style={{
          background: `linear-gradient(135deg, ${T.saffronLight} 0%, #FFF 100%)`,
          border: `2px dashed ${T.saffron}`,
          borderRadius: 14,
          padding: "18px 20px",
          marginBottom: 18,
        }}
      >
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: T.gray600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Student ID / Username
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: T.saffron,
                letterSpacing: "0.08em",
              }}
            >
              {summary.studentId}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 11,
                color: T.gray600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Password
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: T.indigo,
                letterSpacing: "0.08em",
              }}
            >
              {showPassword
                ? password
                : "•".repeat(password.length)}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10
                }}
              >
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 900,
                    color: T.indigo,
                    letterSpacing: "0.08em",
                  }}
                >
                  {showPassword
                    ? password
                    : "•".repeat(password.length)}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setShowPassword(!showPassword)
                  }
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: 18
                  }}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full details table */}

      <div
        style={{
          background: T.white,
          borderRadius: 10,
          border: `1px solid ${T.gray200}`,
          padding: "4px 16px",
          marginBottom: 18,
        }}
      >
        {details.map(([label, value]) => (
          <div
            key={label}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "9px 0",
              borderBottom: `1px solid ${T.gray100}`,
              fontSize: 13,
            }}
          >
            <span style={{ color: T.gray600 }}>{label}</span>

            <span style={{ fontWeight: 700, color: T.gray800 }}>
              {label === "Password" ? "•".repeat(value.length) : value}
            </span>
          </div>
        ))}
      </div>

      <div
        style={{
          background: T.saffronLight,
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 20,
          fontSize: 13,
          color: T.gray800,
          display: "flex",
          gap: 10,
        }}
      >
        <span>⚠️</span>

        <span>
          <strong>Important:</strong> Please save these credentials safely. They
          will be required for future login and examination activities.
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn
          onClick={handleDownloadCredentials}
          variant="outline"
          full
          disabled={downloading}
        >
          {downloading
            ? "Preparing file…"
            : "⬇ Download Registration Details (.docx)"}
        </Btn>

        <Btn onClick={handleGoLogin} full>
          Go to Login →
        </Btn>
      </div>
    </div>
  );
};